"use client";

// FOUNDER-WALK-QA-STAGING-008 · the `sg:pay` answerer for the UN-FRAMED booking surface.
//
// `ExportCheckout` answers the design inside its iframe by posting to `frame.contentWindow`. The
// booking RESULTS route mounts the same design IN THIS DOCUMENT (founder ruling 2026-08-13, "NO
// FRAMES"), so there is no frame to post to — the export's `window.parent.postMessage` is a post to
// THIS window, and the reply goes back the same way. That one difference is the whole of this file;
// the decision about what a submission means is the shared `resolvePayResult`, not a second copy.
//
// RENDERS NOTHING. It is a listener, mounted beside the design surface.

import { useEffect } from "react";
import { useCartStore } from "@/lib/cart-store";
import type { PayOrder } from "@/lib/booking-pay-bridge";
import { resolveDesignPay } from "@/lib/design-pay";

// SNOWCLUB-V101-STAGING-A1 — this answerer used to call `resolvePayResult`, i.e. POST the legacy booking
// engine and, on staging, paint a labelled fake confirmation. It now answers through `resolveDesignPay`:
// the cart the design DISPLAYS (sg-cart-v1) becomes a server-priced order and the card is taken by
// Stripe's hosted PaymentElement on the checkout app. The design is never told "booked" from here.
export default function BookingPayBridge({ mountainCode }: { mountainCode: string }) {
  // The host cart is read only to join a displayed marketplace line to its backend product id.
  const items = useCartStore((s) => s.items);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onMessage = (ev: MessageEvent) => void answer(ev);
    const answer = async (ev: MessageEvent) => {
      // Same-origin only, and only the one shape. A `"*"` reply would hand a customer's booking to
      // whatever else is listening.
      if (ev.origin !== window.location.origin) return;
      const d = ev.data as { type?: string; order?: PayOrder } | null;
      if (d?.type !== "sg:pay") return;
      const result = await resolveDesignPay({ mountainCode, hostItems: items, order: d.order ?? {} });
      window.postMessage({ type: "sg:payresult", result }, window.location.origin);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [items, mountainCode]);

  return null;
}
